import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useId, useState } from "react";
import type {
  PlaceCandidate,
  PlaceMatchSearchResult,
  PlaceMatchStatus,
  VenueRecord,
} from "./placeMatchApi";

export interface PlaceMatchSectionProps {
  status: PlaceMatchStatus;
  /** The last search result, or null before the manager runs a search. */
  search: PlaceMatchSearchResult | null;
  searching?: boolean;
  saving?: boolean;
  onSearch: () => void;
  /** Confirm a candidate by id (the review list) OR an override-supplied known placeId. */
  onConfirm: (placeId: string) => void;
  onNone: () => void;
}

const badge = (tone: "verified" | "review" | "off") =>
  css({
    display: "inline-flex",
    alignItems: "center",
    gap: "1",
    px: "2.5",
    py: "0.5",
    borderRadius: "full",
    fontSize: "sm",
    fontWeight: "medium",
    bg: tone === "verified" ? "green.100" : tone === "review" ? "amber.100" : "gray.100",
    color: tone === "verified" ? "green.800" : tone === "review" ? "amber.800" : "gray.700",
  });

const actionBtn = css({
  px: "3",
  py: "2",
  borderRadius: "md",
  fontWeight: "medium",
  bg: "colorPalette.9",
  color: "white",
  cursor: "pointer",
  _hover: { bg: "colorPalette.10" },
  _disabled: { opacity: 0.5, cursor: "not-allowed" },
});

const secondaryBtn = css({
  px: "3",
  py: "2",
  borderRadius: "md",
  fontWeight: "medium",
  borderWidth: "1px",
  borderColor: "border.default",
  bg: "transparent",
  cursor: "pointer",
  _hover: { bg: "bg.muted" },
  _disabled: { opacity: 0.5, cursor: "not-allowed" },
});

/**
 * The staff Google place-match review surface (`rwg-place-matching`). Shows the current match status
 * (the RwG gate badge + the R5 re-check prompt), runs a candidate search, and drives confirm /
 * none-match / manual override — mounted both as the onboarding match step and re-openable from venue
 * settings. Pure/presentational: the composing page injects the callbacks + async state, so it is
 * trivially unit- and axe-testable. RGAA: a labelled region, native buttons, an explicit override
 * `<form>`, and `aria-live` status.
 */
export function PlaceMatchSection({
  status,
  search,
  searching = false,
  saving = false,
  onSearch,
  onConfirm,
  onNone,
}: PlaceMatchSectionProps) {
  const { t } = useTranslation();
  const overrideId = useId();
  const [overrideValue, setOverrideValue] = useState("");

  const tone = status.gatedIn ? (status.needsReconfirm ? "review" : "verified") : "off";
  const statusLabel = status.gatedIn
    ? status.needsReconfirm
      ? t("placeMatch.badge.review")
      : t("placeMatch.badge.verified")
    : t("placeMatch.badge.off");

  return (
    <section
      aria-labelledby="place-match-heading"
      className={css({ display: "flex", flexDirection: "column", gap: "4" })}
    >
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "3",
        })}
      >
        <h2 id="place-match-heading" className={css({ fontSize: "lg", fontWeight: "bold" })}>
          {t("placeMatch.title")}
        </h2>
        <span className={badge(tone)} aria-live="polite">
          {statusLabel}
        </span>
      </div>

      <p className={css({ color: "fg.muted" })}>{t("placeMatch.intro")}</p>

      {status.needsReconfirm && (
        <output
          className={css({
            display: "block",
            p: "3",
            borderRadius: "md",
            bg: "amber.50",
            color: "amber.900",
            borderWidth: "1px",
            borderColor: "amber.200",
          })}
        >
          {t("placeMatch.reconfirmPrompt")}
        </output>
      )}

      <div>
        <button
          type="button"
          className={actionBtn}
          onClick={onSearch}
          disabled={searching || saving}
          data-testid="place-match-search"
        >
          {searching ? t("placeMatch.searching") : t("placeMatch.searchAction")}
        </button>
      </div>

      {search && !search.searchable && (
        <p role="alert" className={css({ color: "fg.error" })}>
          {t("placeMatch.notSearchable")}
        </p>
      )}

      {search?.searchable && (
        <div className={css({ display: "flex", flexDirection: "column", gap: "3" })}>
          {search.venue && <VenueRecordCard record={search.venue} />}

          <h3 className={css({ fontSize: "md", fontWeight: "semibold" })}>
            {t("placeMatch.candidatesHeading")}
          </h3>

          {search.candidates.length === 0 ? (
            <p className={css({ color: "fg.muted" })}>{t("placeMatch.noCandidates")}</p>
          ) : (
            <ul
              className={css({
                display: "flex",
                flexDirection: "column",
                gap: "2",
                listStyle: "none",
              })}
            >
              {search.candidates.map((c) => (
                <CandidateRow
                  key={c.placeId}
                  candidate={c}
                  selected={status.placeId === c.placeId}
                  saving={saving}
                  onConfirm={() => onConfirm(c.placeId)}
                />
              ))}
            </ul>
          )}

          <button
            type="button"
            className={secondaryBtn}
            onClick={onNone}
            disabled={saving}
            data-testid="place-match-none"
          >
            {t("placeMatch.noneAction")}
          </button>
        </div>
      )}

      {/* The raw Google Place ID override is an escape hatch, not the primary path — tuck it behind a
          "can't find your business?" disclosure so most owners just pick from the suggested matches. */}
      <details className={css({ mt: "2" })}>
        <summary className={css({ cursor: "pointer", fontSize: "sm", color: "fg.muted", py: "1" })}>
          {t("placeMatch.overrideDisclosure")}
        </summary>
        <form
          className={css({ display: "flex", flexDirection: "column", gap: "2", mt: "2" })}
          onSubmit={(e) => {
            e.preventDefault();
            const v = overrideValue.trim();
            if (v) onConfirm(v);
          }}
        >
          <label htmlFor={overrideId} className={css({ fontWeight: "medium" })}>
            {t("placeMatch.overrideLabel")}
          </label>
          <div className={css({ display: "flex", gap: "2" })}>
            <input
              id={overrideId}
              value={overrideValue}
              onChange={(e) => setOverrideValue(e.target.value)}
              placeholder={t("placeMatch.overridePlaceholder")}
              className={css({
                flex: "1",
                px: "3",
                py: "2",
                borderRadius: "md",
                borderWidth: "1px",
                borderColor: "border.default",
              })}
            />
            <button
              type="submit"
              className={secondaryBtn}
              disabled={saving || overrideValue.trim() === ""}
            >
              {t("placeMatch.overrideAction")}
            </button>
          </div>
        </form>
      </details>
    </section>
  );
}

function VenueRecordCard({ record }: { record: VenueRecord }) {
  const { t } = useTranslation();
  return (
    <div
      className={css({
        p: "3",
        borderRadius: "md",
        borderWidth: "1px",
        borderColor: "border.default",
        bg: "bg.subtle",
      })}
    >
      <p className={css({ fontSize: "sm", color: "fg.muted", mb: "1" })}>
        {t("placeMatch.yourRecord")}
      </p>
      <p className={css({ fontWeight: "semibold" })}>{record.legalName}</p>
      <p className={css({ color: "fg.muted" })}>{record.formattedAddress}</p>
      <p className={css({ color: "fg.muted", fontSize: "sm" })}>{record.category}</p>
    </div>
  );
}

function CandidateRow({
  candidate,
  selected,
  saving,
  onConfirm,
}: {
  candidate: PlaceCandidate;
  selected: boolean;
  saving: boolean;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "3",
        p: "3",
        borderRadius: "md",
        borderWidth: "1px",
        borderColor: selected ? "colorPalette.9" : "border.default",
      })}
    >
      <div>
        <p className={css({ fontWeight: "semibold" })}>{candidate.name}</p>
        <p className={css({ color: "fg.muted" })}>{candidate.formattedAddress}</p>
        {candidate.category && (
          <p className={css({ color: "fg.muted", fontSize: "sm" })}>{candidate.category}</p>
        )}
      </div>
      <button type="button" className={actionBtn} onClick={onConfirm} disabled={saving}>
        {selected ? t("placeMatch.confirmedAction") : t("placeMatch.confirmAction")}
      </button>
    </li>
  );
}
